import { TokensApi, WalletInsightsApi, createPortfolioClient, } from "@blockdaemon-internal/portfolio-ts-client";
import { getLogger } from "../../shared/logger";
import { parseChainIdToCaip2 } from "../chains/chain-service";
import { DEFAULT_BASE_PATH } from "../../shared/constants";
import { executeGenericOperation } from "../../shared/executor";
import { RetryService } from "../../shared/retry";
import { _estimateTxFee as _estimateTxFeeInternal } from "./internal/estimate-tx-fee";
import { _getAllowance as _getAllowanceInternal } from "./internal/get-allowance";
import { _getAssets as _getAssetsInternal } from "./internal/get-assets";
import { _getBalance as _getBalanceInternal } from "./internal/get-balance";
import { _getBytecode as _getBytecodeInternal } from "./internal/get-bytecode";
import { _getGasPrice as _getGasPriceInternal } from "./internal/get-gas-price";
import { createUniversalFeeEstimatorApi } from "./internal/universal-fee-estimator-api";
import { _waitForSolanaTransaction as _waitForSolanaTransactionInternal } from "./internal/wait-for-solana-transaction";
import { _waitForTransactionReceipt as _waitForTransactionReceiptInternal } from "./internal/wait-for-transaction-receipt";
/**
 * Common module for shared functionality across all modules.
 *
 * @remarks
 * The CommonModule provides foundational blockchain operations that are shared
 * across all other SDK modules. It serves as the core utility layer for common
 * blockchain tasks including balance queries, token pricing, address validation,
 * transaction building, and comprehensive fee estimation.
 *
 * **Key Features:**
 * - **Balance Queries**: Retrieve native and token balances for any address
 * - **Token Pricing**: Get real-time market prices for supported tokens
 * - **Token Metadata**: Access comprehensive token information and filtering
 * - **Address Validation**: Validate blockchain addresses
 * - **Transaction Building**: Build and prepare blockchain transactions
 * - **Gas Price Information**: Fetch current gas price recommendations
 * - **Fee Estimation**: Comprehensive transaction fee estimation with multiple methods
 * - **Health Monitoring**: Service health checks and status monitoring
 *
 * **Fee Estimation:**
 * `estimateTxFee` sources EIP-1559 fees from the Blockdaemon Universal REST
 * fee estimator (`/tx/estimate_fee`) on EVM chains and from Solana JSON-RPC
 * (`getFeeForMessage` + `getRecentPrioritizationFees`) on Solana. It returns
 * a flat `slow` / `medium` / `fast` breakdown; each tier carries the
 * canonical EIP-1559 per-gas fields (`baseFeePerGas`,
 * `maxPriorityFeePerGas`, `maxFeePerGas`).
 *
 * **Integration Examples:**
 * This module integrates seamlessly with other SDK modules:
 * - DefiModule uses CommonModule for token pricing and fee estimation
 * - StakingModule uses CommonModule for balance queries and transaction building
 * - All modules benefit from address validation and gas price information
 *
 * @example
 * **Basic Setup and Gas Price Information**
 * ```typescript
 * import { CommonModule } from "@blockdaemon/bd-sdk";
 *
 * const common = new CommonModule({
 *   chainService: chainService,
 *   bdApiKey: "your-blockdaemon-api-key"
 * });
 *
 * // Get current gas prices
 * const gasPrice = await common.getGasPrice({
 *   chainId: "1"
 * });
 *
 * console.log(`Standard gas price: ${gasPrice.standard} wei`);
 * console.log(`Fast gas price: ${gasPrice.fast} wei`);
 * ```
 *
 * @example
 * **Comprehensive Fee Estimation**
 * ```typescript
 * const fees = await common.estimateTxFee({
 *   transaction: {
 *     to: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
 *     from: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
 *     value: "1000000000000000000",
 *     data: "0xa9059cbb..."
 *   },
 *   chainId: "1"
 * });
 *
 * console.log(`Medium maxFeePerGas: ${fees.medium.maxFeePerGas}`);
 * console.log(`Fast maxPriorityFeePerGas: ${fees.fast.maxPriorityFeePerGas}`);
 * ```
 *
 * @example
 * **Balance and Portfolio Analysis**
 * ```typescript
 * // Get comprehensive portfolio balances
 * const balances = await common.getBalance({
 *   address: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
 *   chainID: "1", // Ethereum mainnet
 *   tokens: "USDC,DAI,USDT" // Filter specific tokens
 * });
 *
 * console.log(`Native balance: ${balances.data.native}`);
 * console.log(`Token count: ${balances.data.evmTokens.length}`);
 * ```
 *
 * @public
 */
export class CommonModule {
    chainService;
    logger;
    portfolioWalletSdk;
    portfolioTokensSdk;
    options;
    retry;
    feeEstimatorApi;
    /**
     * Creates a new CommonModule instance.
     *
     * @param options - Configuration options for the common module
     * @throws {Error} If the BD API key is missing or empty
     */
    constructor(options) {
        this.logger = options.logger || getLogger({ level: "info", label: "bd-sdk-common-module" });
        if (!options.bdApiKey || options.bdApiKey.trim() === "") {
            this.logger.warn("BD API key is not set. Some CommonModule operations may fail with 401 Unauthorized.");
        }
        this.options = options;
        this.chainService = options.chainService;
        const portfolioClient = createPortfolioClient({
            bearerToken: options.bdApiKey ?? "",
            baseUrl: options.basePath ?? DEFAULT_BASE_PATH,
        });
        this.portfolioWalletSdk = new WalletInsightsApi(portfolioClient.getConfiguration());
        this.portfolioTokensSdk = new TokensApi(portfolioClient.getConfiguration());
        // Build the Universal REST `FeeEstimatorApi` once at construction time.
        // The factory imports only from `@ubiquity/ubiquity-ts-client/dist/src/generated`
        // (axios-only, browser-safe) — never the package's top-level barrel,
        // which would pull in `bitcore-lib`/`ethers`/`isomorphic-ws`.
        this.feeEstimatorApi = createUniversalFeeEstimatorApi({
            bdApiKey: options.bdApiKey ?? "",
            basePath: options.basePath ?? DEFAULT_BASE_PATH,
        });
        this.retry = new RetryService({
            logger: this.logger,
            wait: { maxAttempts: 2, baseDelayMs: 200, jitterMs: 200 },
            labelPrefix: "retry-service-common",
        });
    }
    /**
     * Retrieves comprehensive balance information for a specified blockchain address.
     *
     * @remarks
     * This method fetches both native cryptocurrency balances and ERC-20/ERC-1155
     * token balances, providing a complete portfolio view. The response includes
     * current market valuations in USD and detailed token metadata.
     *
     * **Features:**
     * - Native balance retrieval (ETH, BTC, etc.)
     * - ERC-20 and ERC-1155 token balance aggregation
     * - Real-time USD price calculation
     * - Token metadata including decimals, symbols, and logos
     * - Optional token filtering for focused queries
     * - Multi-chain support across all Blockdaemon networks
     *
     * **Supported Networks:**
     * - Ethereum (chainID: "1")
     * - Polygon (chainID: "137")
     * - Arbitrum (chainID: "42161")
     * - Optimism (chainID: "10")
     * @param request - The balance query parameters
     * @returns Promise resolving to comprehensive balance information
     * @throws {Error} If the SDK configuration is not initialized
     */
    async getBalance(request) {
        if (!this.options)
            throw new Error("SDK config not initialized");
        const chainIdStr = String(request.chainId ?? "").trim();
        if (chainIdStr === "") {
            throw new Error("Chain ID is required. Provide a valid chain ID (e.g., '1', '137', 'eip155:1', 'solana:mainnet').");
        }
        const normalizedChainId = parseChainIdToCaip2(chainIdStr, "chainId");
        return executeGenericOperation("getBalance", this.logger, _getBalanceInternal({ ...request, chainId: normalizedChainId }, this.logger, this.portfolioWalletSdk));
    }
    /**
     * Retrieves token metadata and pricing information with filtering capabilities.
     *
     * @remarks
     * Provides access to
     * Blockdaemon's curated token database with verified tokens, comprehensive metadata,
     * pricing, categorization, and UI assets.
     *
     * **Key Features:**
     * - Single token lookup by address
     * - Multiple token discovery and filtering
     * - Advanced filtering by chain, symbol, address, or category tags
     * - Rich metadata including names, symbols, decimals, logos, descriptions
     * - Categorization tags like "stablecoin", "defi", "gamefi", etc.
     * - Real-time USD pricing when available
     * - Verification status ensuring only quality tokens
     *
     * **Supported Networks:**
     * - Ethereum (chainID: "1")
     * - Polygon (chainID: "137")
     * - Arbitrum (chainID: "42161")
     * - Optimism (chainID: "10")
     * - Hoodi Testnet (chainID: "560048")
     * - And many more EVM-compatible networks
     *
     * **Available Categories:**
     * - "stablecoin": USD-pegged stable tokens
     * - "defi": Decentralized Finance tokens
     * - "gamefi": Gaming and NFT tokens
     * - "infrastructure": Blockchain infrastructure tokens
     * - "exchange": Exchange and trading tokens
     * - "yield": Yield farming and staking tokens
     *
     * @example
     * **Get single token price**
     * ```typescript
     * const assets = await commonModule.getAssets({
     *   tokenAddress: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
     *   chainId: "eip155:1"
     * });
     * const usdc = assets.data[0];
     * console.log(`USDC price: $${usdc.priceUSD}`);
     * ```
     *
     * @example
     * **Get multiple tokens by filter**
     * ```typescript
     * const assets = await commonModule.getAssets({
     *   chainId: "eip155:1",
     *   tagLimit: ["stablecoin"]
     * });
     * console.log(`Found ${assets.data.length} stablecoins`);
     * ```
     *
     * @param request - The token search and filter parameters
     * @returns Promise resolving to filtered token metadata array
     * @throws {Error} If the SDK configuration is not initialized
     * @throws {Error} If the API request fails
     */
    async getAssets(request) {
        if (!this.options)
            throw new Error("SDK config not initialized");
        return executeGenericOperation("getAssets", this.logger, _getAssetsInternal(request, this.logger, this.portfolioTokensSdk));
    }
    /**
     * Retrieves current gas price recommendations via direct RPC.
     *
     * @remarks
     * Queries on-chain fee data through `ChainService` and derives slow, standard,
     * and fast price tiers based on current network conditions. Supports EIP-1559
     * fields when available and falls back to legacy gasPrice otherwise.
     *
     * @param request - Gas price request with chainId (numeric or CAIP-2) and optional network
     * @returns Promise resolving to gas price information with speed tiers
     * @throws {Error} If SDK is not initialized, validation fails, or RPC fee data is unavailable
     *
     * @example
     * **Basic Usage**
     * ```typescript
     * const gasPrice = await common.getGasPrice({ chainId: "1" });
     *
     * console.log(`Slow: ${gasPrice.slow} wei`);
     * console.log(`Standard: ${gasPrice.standard} wei`);
     * console.log(`Fast: ${gasPrice.fast} wei`);
     * ```
     *
     * @example
     * **Convert to Gwei for readability**
     * ```typescript
     * const gasPrice = await common.getGasPrice({ chainId: "1" });
     * const toGwei = (wei: number) => (wei / 1e9).toFixed(2);
     *
     * console.log(`Standard: ${toGwei(gasPrice.standard)} Gwei`);
     * ```
     *
     * @example
     * **Multi-chain monitoring**
     * ```typescript
     * const chains = ["1", "137", "8453"]; // Ethereum, Polygon, Base
     *
     * for (const chainId of chains) {
     *   const gasPrice = await common.getGasPrice({ chainId });
     *   console.log(`Chain ${chainId}: ${(gasPrice.standard / 1e9).toFixed(2)} Gwei`);
     * }
     * ```
     */
    async getGasPrice(request) {
        if (!this.options)
            throw new Error("SDK config not initialized");
        return executeGenericOperation("getGasPrice", this.logger, _getGasPriceInternal(request, this.chainService, this.logger, this.retry));
    }
    /**
     * Estimates EIP-1559 transaction fees.
     *
     * @remarks
     * Supports both EVM and Solana chains. The response is a flat
     * `slow` / `medium` / `fast` breakdown; the caller picks the tier at the
     * call site.
     *
     * **EVM chains** — sources per-tier `maxPriorityFeePerGas` and derives
     * `baseFeePerGas` (`max_total_fee - max_priority_fee`) from the Blockdaemon
     * Universal REST fee estimator (`/tx/estimate_fee`). Applies a per-tier
     * base-fee buffer and a tip floor to produce `maxFeePerGas`. Also runs
     * `eth_estimateGas` in parallel and returns the gas units string on
     * `gasEstimate`.
     *
     * **Solana chains** — uses `getFeeForMessage` for the base transaction fee
     * and `getRecentPrioritizationFees` for priority fee tiers. The
     * `transaction.data` field must contain the base64-encoded serialized
     * transaction (as returned by `buildStakeTx`, `buildWithdrawTx`, etc.).
     * Response values are in lamports; `maxPriorityFeePerGas` is in
     * microLamports per Compute Unit. `gasEstimate` is the required signature
     * slot count for the serialized transaction.
     *
     * @param request - Transaction details and chain ID for fee estimation
     * @returns Flat `slow` / `medium` / `fast` tier breakdown plus `gasEstimate`
     * @throws {Error} When chain unsupported, transaction invalid, or estimation fails
     *
     * @example
     * **EVM — ETH transfer**
     * ```typescript
     * const tx = {
     *   to: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
     *   from: "0xYourAddress",
     *   value: "1000000000000000000"
     * };
     * const fees = await commonModule.estimateTxFee({
     *   transaction: tx,
     *   chainId: "eip155:1"
     * });
     *
     * await wallet.sendTransaction({
     *   ...tx,
     *   maxFeePerGas: fees.fast.maxFeePerGas,
     *   maxPriorityFeePerGas: fees.fast.maxPriorityFeePerGas,
     * });
     * ```
     *
     * @example
     * **Solana — staking transaction**
     * ```typescript
     * const stakeTx = await sdk.staking.buildStakeTx({ ... });
     * const fees = await commonModule.estimateTxFee({
     *   transaction: {
     *     to: stakeTx.to,
     *     from: userSolanaAddress,
     *     data: stakeTx.data,
     *     value: "0"
     *   },
     *   chainId: "solana:mainnet"
     * });
     * ```
     */
    async estimateTxFee(request) {
        if (!this.options)
            throw new Error("SDK config not initialized");
        return executeGenericOperation("estimateTxFee", this.logger, _estimateTxFeeInternal(request, this.chainService, this.logger, this.retry, {
            feeEstimatorApi: this.feeEstimatorApi,
        }));
    }
    /**
     * Retrieves the ERC-20 token allowance for a spender on behalf of an owner.
     *
     * @remarks
     * This method queries the on-chain allowance that an owner has granted to a spender
     * for a specific ERC-20 token. This is essential for validating that sufficient
     * approval exists before executing token transfers, swaps, or DeFi operations.
     *
     * **Features:**
     * - Direct on-chain RPC query for real-time allowance data
     * - Support for all EVM-compatible chains
     * - Automatic retry handling for RPC failures
     * - Returns raw token units (not decimal-adjusted)
     *
     * **Common Use Cases:**
     * - Checking if approval is needed before a DEX swap
     * - Validating allowance before lending/borrowing operations
     * - Monitoring token approvals for security purposes
     *
     * @param request - The allowance query parameters
     * @returns Promise resolving to allowance information
     * @throws {Error} If the SDK configuration is not initialized
     * @throws {Error} If the chain is not supported
     * @throws {Error} If the contract call fails
     *
     * @example
     * **Check USDC allowance for a DEX router**
     * ```typescript
     * const allowance = await common.getAllowance({
     *   tokenAddress: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", // USDC
     *   ownerAddress: "0x742d35Cc6634C0532925a3b8D4C9db96C4b4d8b6",
     *   spenderAddress: "0x1111111254EEB25477B68fb85Ed929f73A960582", // 1inch
     *   chainId: "1"
     * });
     *
     * console.log(`Current allowance: ${allowance.allowance}`);
     *
     * // Check if sufficient for a swap
     * const requiredAmount = BigInt("1000000"); // 1 USDC
     * if (BigInt(allowance.allowance) >= requiredAmount) {
     *   console.log("Sufficient allowance, can proceed");
     * } else {
     *   console.log("Need to approve more tokens");
     * }
     * ```
     *
     * @example
     * **Check allowance on Polygon**
     * ```typescript
     * const allowance = await common.getAllowance({
     *   tokenAddress: "0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174", // USDC on Polygon
     *   ownerAddress: userAddress,
     *   spenderAddress: aavePoolAddress,
     *   chainId: "137" // or "eip155:137"
     * });
     * ```
     */
    async getAllowance(request) {
        if (!this.options)
            throw new Error("SDK config not initialized");
        return executeGenericOperation("getAllowance", this.logger, _getAllowanceInternal(request, this.chainService, this.logger, this.retry));
    }
    /**
     * Retrieves the bytecode deployed at a specific address.
     *
     * @remarks
     * This method queries the on-chain bytecode to verify whether a contract
     * exists at the given address. This is useful for validating token contract
     * addresses before attempting to interact with them.
     *
     * **Features:**
     * - Direct on-chain RPC query for real-time bytecode data
     * - Support for all EVM-compatible chains
     * - Automatic retry handling for RPC failures
     * - Convenience `isContract` field for easy validation
     *
     * **Use Cases:**
     * - Verifying a token contract exists before balance/allowance queries
     * - Validating contract addresses in user input
     * - Checking if an address is a contract or EOA (externally owned account)
     *
     * @param request - The bytecode query parameters
     * @returns Promise resolving to bytecode information
     * @throws {Error} If the SDK configuration is not initialized
     * @throws {Error} If the chain is not supported
     * @throws {Error} If the RPC call fails
     *
     * @example
     * **Verify a token contract exists**
     * ```typescript
     * const result = await common.getBytecode({
     *   address: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", // USDC
     *   chainId: "1"
     * });
     *
     * if (result.isContract) {
     *   console.log("Contract exists, can proceed with token operations");
     * } else {
     *   console.log("No contract at this address - invalid token");
     * }
     * ```
     *
     * @example
     * **Validate before token interaction**
     * ```typescript
     * const bytecodeResult = await common.getBytecode({
     *   address: tokenAddress,
     *   chainId: "1"
     * });
     *
     * if (!bytecodeResult.isContract) {
     *   throw new Error("Invalid token address - no contract deployed");
     * }
     *
     * // Safe to proceed with token operations
     * const allowance = await common.getAllowance({ ... });
     * ```
     */
    async getBytecode(request) {
        if (!this.options)
            throw new Error("SDK config not initialized");
        return executeGenericOperation("getBytecode", this.logger, _getBytecodeInternal(request, this.chainService, this.logger, this.retry));
    }
    /**
     * Waits for a transaction to be included in a block and returns the receipt.
     *
     * @remarks
     * This method monitors a transaction until it is confirmed on the blockchain,
     * then returns the transaction receipt with execution details. This is useful
     * for tracking transaction status after broadcasting.
     *
     * **Features:**
     * - Waits for transaction confirmation (default: 1 confirmation)
     * - Configurable number of confirmations to wait for
     * - Returns comprehensive receipt with status, gas used, logs, etc.
     *
     * **Use Cases:**
     * - Confirming a swap transaction completed successfully
     * - Waiting for a staking transaction to be mined
     * - Verifying contract deployment succeeded
     * - Monitoring any blockchain transaction
     *
     * @param request - The transaction receipt request parameters
     * @returns Promise resolving to the transaction receipt, or null if not found
     * @throws {Error} If the SDK configuration is not initialized
     * @throws {Error} If the chain is not supported
     * @throws {Error} If the RPC call fails
     *
     * @example
     * **Wait for a transaction to be confirmed**
     * ```typescript
     * const receipt = await common.waitForTransactionReceipt({
     *   txHash: "0x1234567890abcdef...",
     *   chainId: "1"
     * });
     *
     * if (receipt && receipt.status === "success") {
     *   console.log("Transaction succeeded!");
     *   console.log(`Block: ${receipt.blockNumber}`);
     *   console.log(`Gas used: ${receipt.gasUsed}`);
     * } else if (receipt && receipt.status === "reverted") {
     *   console.log("Transaction reverted");
     * }
     * ```
     *
     * @example
     * **Wait with multiple confirmations**
     * ```typescript
     * const receipt = await common.waitForTransactionReceipt({
     *   txHash: "0x1234567890abcdef...",
     *   chainId: "1",
     *   confirmations: 3 // Wait for 3 confirmations
     * });
     * ```
     */
    async waitForTransactionReceipt(request) {
        if (!this.options)
            throw new Error("SDK config not initialized");
        return executeGenericOperation("waitForTransactionReceipt", this.logger, _waitForTransactionReceiptInternal(request, this.chainService, this.logger));
    }
    /**
     * Waits until a Solana transaction reaches `"finalized"` commitment.
     *
     * @remarks
     * Uses the Blockdaemon Solana RPC URL from {@link ChainService.getSolanaRpcUrl} and
     * signs HTTP requests with the SDK API key when configured. Callers do not pass a
     * {@link import("@solana/web3.js").Connection}.
     *
     * @param request - Signature (base58) and Solana CAIP-2 chain ID
     * @throws {Error} If the SDK configuration is not initialized, chain ID is invalid,
     *   or confirmation fails
     */
    async waitForSolanaTransaction(request) {
        if (!this.options)
            throw new Error("SDK config not initialized");
        return executeGenericOperation("waitForSolanaTransaction", this.logger, _waitForSolanaTransactionInternal(request, this.chainService, this.options.bdApiKey, this.logger));
    }
}
//# sourceMappingURL=data:application/json;base64,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